import baseAPI from "@/api/base";
import { AssignRepToContacts, EditContact, GetContact, RegisterContact } from "@/dtos/Contact.dto";
import type {
  AddContactChatReactionPayload,
  ContactChatMessagesResponse,
  ContactChatReactionResponse,
  InteractionCounts,
  RemoveContactChatReactionPayload,
  SendContactChatMessagePayload,
} from "@/dtos/ContactChat.dto";
import type { HubSpotListsParams, ImportFromHubSpotDto } from "@/dtos/HubSpot.dto";
import { ContactEventHistory } from "@/dtos/Unipile.dto";
import {
  ADD_CONTACT_CHAT_REACTION_URL,
  ADD_CONTACT_PHONE_NUMBER_URL,
  ASSIGN_REP_TO_CONTACTS_URL,
  DELETE_CONTACT_PHONE_NUMBER_URL,
  DELETE_CONTACT_URL,
  EDIT_CONTACT_PHONE_NUMBER_URL,
  EDIT_CONTACT_URL,
  GET_CONTACT_CHAT_MESSAGES_URL,
  GET_CONTACT_EVENTS_URL,
  GET_CONTACT_INTERACTION_COUNTS_URL,
  GET_CONTACT_TIMEZONES_URL,
  GET_CONTACT_URL,
  GET_ENGAGEMENT_SUMMARY_URL,
  GET_HUBSPOT_LISTS_URL,
  IMPORT_CONTACT_URL,
  IMPORT_FROM_HUBSPOT_URL,
  MARK_CHANNEL_NOTIFICATIONS_SEEN_URL,
  MARK_CONTACT_CHAT_READ_URL,
  MARK_EMAIL_NOTIFICATIONS_SEEN_URL,
  MERGE_CONTACTS_URL,
  REGISTER_CONTACT_URL,
  REMOVE_CONTACT_CHAT_REACTION_URL,
  SAVE_CONTACT_PROFILE_PICTURE_URL,
  SEND_CONTACT_CHAT_MESSAGE_URL,
  TOGGLE_CONTACT_IS_ACTIVE_URL,
} from "@/utils/urls";
import { AxiosResponse } from "axios";

class Contact {
  static getEngagementSummary = async (contactIds: string[]): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_ENGAGEMENT_SUMMARY_URL, {
      params: { contactIds: contactIds.join(",") },
    });
    return response;
  };

  static markEmailNotificationsSeen = async (
    contactId: string,
    payload: { threadId?: string; emailId?: string; emailIds?: string[] }
  ): Promise<AxiosResponse> => {
    const url = MARK_EMAIL_NOTIFICATIONS_SEEN_URL.replace(":id", contactId);
    const response: AxiosResponse = await baseAPI.post(url, payload);
    return response;
  };

  static markChannelNotificationsSeen = async (
    contactId: string,
    channel: "linkedin" | "whatsapp"
  ): Promise<AxiosResponse> => {
    const url = MARK_CHANNEL_NOTIFICATIONS_SEEN_URL.replace(":id", contactId);
    const response: AxiosResponse = await baseAPI.post(url, { channel });
    return response;
  };

  static getContacts = async (getContact: GetContact): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_CONTACT_URL, {
      params: getContact,
    });
    return response;
  };

  static getContactEnrollments = async (contactId: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(
      `${GET_CONTACT_URL}/${contactId}/enrollments`
    );
    return response;
  };

  static getContactTimezones = async (organizationId: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_CONTACT_TIMEZONES_URL, {
      params: { organizationId },
    });
    return response;
  };

  static registerContact = async (registerContact: RegisterContact): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(REGISTER_CONTACT_URL, {
      ...registerContact,
    });
    return response;
  };

  static importContact = async (formData: FormData): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(IMPORT_CONTACT_URL, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  };

  static getHubSpotContactLists = async (params: HubSpotListsParams): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_HUBSPOT_LISTS_URL, {
      params,
    });
    return response;
  };

  static importFromHubSpot = async (data: ImportFromHubSpotDto): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(IMPORT_FROM_HUBSPOT_URL, data);
    return response;
  };

  static editContact = async (editContact: EditContact): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(EDIT_CONTACT_URL, {
      ...editContact,
    });
    return response;
  };

  static addContactPhoneNumber = async (editContact: EditContact): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(ADD_CONTACT_PHONE_NUMBER_URL, {
      ...editContact,
    });
    return response;
  };

  static editContactPhoneNumber = async (editContact: EditContact): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(EDIT_CONTACT_PHONE_NUMBER_URL, {
      ...editContact,
    });
    return response;
  };

  static toggleContactIsActive = async (editContact: EditContact): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.patch(TOGGLE_CONTACT_IS_ACTIVE_URL, {
      ...editContact,
    });
    return response;
  };

  static deleteContactPhoneNumber = async (value: Record<string, any>): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.delete(
      `${DELETE_CONTACT_PHONE_NUMBER_URL}/${value.id}/${value.phoneNumber}`
    );
    return response;
  };

  static deleteContact = async (id: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.delete(`${DELETE_CONTACT_URL}/${id}`);
    return response;
  };

  static saveProfilePicture = async (
    contactId: string,
    pictureUrl: string
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(
      `${SAVE_CONTACT_PROFILE_PICTURE_URL}/${contactId}/profile-picture`,
      { pictureUrl }
    );
    return response;
  };

  static assignRepToContacts = async (data: AssignRepToContacts): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(ASSIGN_REP_TO_CONTACTS_URL, {
      ...data,
    });
    return response;
  };

  static mergeContacts = async (data: {
    primaryId: string;
    secondaryId: string;
  }): Promise<AxiosResponse> => {
    return baseAPI.post(MERGE_CONTACTS_URL, data);
  };

  static exportContacts = async (params: {
    contactIds?: string[];
    customFieldIds?: string[];
  }): Promise<AxiosResponse> => {
    return baseAPI.post("/report/contacts/export", params, { responseType: "blob" });
  };

  static getEmailTrackingEvents = async (params: {
    email_id: string;
    contactId?: string; // Optional, for filtering by contact
    eventType?: "EMAIL_OPENED" | "EMAIL_CLICKED";
  }): Promise<AxiosResponse<{ status: boolean; data: ContactEventHistory[] }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("email_id", params.email_id);
    if (params.eventType) queryParams.append("eventType", params.eventType);
    if (params.contactId) queryParams.append("contactId", params.contactId);

    const url = GET_CONTACT_EVENTS_URL + `?${queryParams.toString()}`;
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  static getInteractionCounts = async (
    contactId: string
  ): Promise<AxiosResponse<{ data: InteractionCounts }>> => {
    const url = GET_CONTACT_INTERACTION_COUNTS_URL.replace(":id", contactId);
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  static getChatMessages = async (
    contactId: string
  ): Promise<AxiosResponse<{ data: ContactChatMessagesResponse }>> => {
    const url = GET_CONTACT_CHAT_MESSAGES_URL.replace(":id", contactId);
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  static sendChatMessage = async ({
    contactId,
    message,
  }: SendContactChatMessagePayload): Promise<AxiosResponse> => {
    const url = SEND_CONTACT_CHAT_MESSAGE_URL.replace(":id", contactId);
    const response: AxiosResponse = await baseAPI.post(url, { message });
    return response;
  };

  static markChatRead = async (contactId: string): Promise<AxiosResponse> => {
    const url = MARK_CONTACT_CHAT_READ_URL.replace(":id", contactId);
    const response: AxiosResponse = await baseAPI.post(url);
    return response;
  };

  static addChatReaction = async ({
    contactId,
    messageId,
    reaction,
  }: AddContactChatReactionPayload): Promise<
    AxiosResponse<{ data: ContactChatReactionResponse }>
  > => {
    const url = ADD_CONTACT_CHAT_REACTION_URL.replace(":id", contactId).replace(
      ":messageId",
      messageId
    );
    const response: AxiosResponse = await baseAPI.post(url, { reaction });
    return response;
  };

  static removeChatReaction = async ({
    contactId,
    messageId,
  }: RemoveContactChatReactionPayload): Promise<
    AxiosResponse<{ data: ContactChatReactionResponse }>
  > => {
    const url = REMOVE_CONTACT_CHAT_REACTION_URL.replace(":id", contactId).replace(
      ":messageId",
      messageId
    );
    const response: AxiosResponse = await baseAPI.delete(url);
    return response;
  };
}

export default Contact;
